import {LinkNode, LinkAttributes, SerializedLinkNode} from '@lexical/link';
import {NodeKey} from 'lexical';

export type SerializedCustomLinkNode = SerializedLinkNode;

/**
 * Custom Link Node that extends Lexical's LinkNode to bypass URL sanitization
 * for Priority protocol URLs while preserving all other LinkNode functionality.
 */
export class CustomLinkNode extends LinkNode {
    static getType(): string {
        return 'custom-link';
    }

    static clone(node: CustomLinkNode): CustomLinkNode {
        return new CustomLinkNode(
            node.__url,
            {
                target: node.__target,
                rel: node.__rel,
                title: node.__title
            },
            node.__key
        );
    }

    constructor(url: string = '', attributes: LinkAttributes = {}, key?: NodeKey) {
        super(url, attributes, key);
    }

    /**
     * Override sanitizeUrl to bypass sanitization for Priority protocol URLs
     * while keeping sanitization for all other URLs
     */
    sanitizeUrl(url: string): string {
        if (isPriorityProtocolUrl(url)) {
            // For Priority protocol URLs, return the URL as-is without sanitization
            return url;
        }
        // For all other URLs, use the parent's sanitization logic
        return super.sanitizeUrl(url);
    }

    // Override DOM import for <a>
    static importDOM() {
        return {
            a: (node: Node) => {
                const domNode = node as HTMLAnchorElement;
                const href = domNode.getAttribute('href') ?? '';

                if (isPriorityProtocolUrl(href)) {
                    return {
                        conversion: () => ({node: new CustomLinkNode(href)}),
                        priority: 3 as const
                    };
                }

                // Fallback: use default LinkNode conversion
                return null;
            }
        };
    }

    static importJSON(serializedNode: SerializedCustomLinkNode): CustomLinkNode {
        const {url, target, rel, title} = serializedNode;
        const node = new CustomLinkNode(url, {target, rel, title});
        return node;
    }

    exportJSON(): SerializedCustomLinkNode {
        return {
            ...super.exportJSON(),
            type: 'custom-link'
        };
    }
}

/**
 * Checks if a URL is a Priority protocol URL that should use CustomLinkNode
 * @param href - The URL to check
 * @returns true if it's a Priority protocol URL
 */
export function isPriorityProtocolUrl(href: string): boolean {
    if (!href) return false;

    // Remove whitespaces after colon and check for priority:priform pattern
    const cleanHref = href.replace(/:\s+/g, ':');
    return cleanHref.includes('priority:priform');
}
